import { useContext, useEffect, useState } from "react";
import {
  MdAdd,
  MdAddCircleOutline,
  MdOutlineAccountBalanceWallet,
  MdOutlineArrowCircleRight,
  MdOutlinePersonAddAlt,
  MdRemove,
} from "react-icons/md";
import { ContactListType, CreateEditContactType, SchoolListType } from "utils/interfaces";
import { usePlatform } from "components/utils/PlatformContext";
import { AnimatePresence } from "framer-motion";
import { MainContext } from "components/utils/Contexts";
import { singlify } from "utils/utils";
import { updateRecordRequest } from "services/apiRequests";
import AddContactsPanel from "../pop-up-panels/add-contact-pop-up";
import CreateEditUser from "../pop-up-panels/common/CreateEditUser";
import InputBadgeImage from "components/main-view/utils/placeholders/InputBadgeImage";
import UserCardForm from "../cards/UserFormCard";

interface BasicDetailsProps {
  selectedData?: SchoolListType | null;
  schoolName: string;
  logo: string | null;
  assignedLicenses: number;
  contacts: ContactListType[];
  admins: ContactListType[];
  setSchoolName: React.Dispatch<React.SetStateAction<string>>;
  setLogo: React.Dispatch<React.SetStateAction<string | null>>;
  setAssignedLicenses: React.Dispatch<React.SetStateAction<number>>;
  setContacts: React.Dispatch<React.SetStateAction<ContactListType[]>>;
  setAdmins: React.Dispatch<React.SetStateAction<ContactListType[]>>;
  setStage: React.Dispatch<React.SetStateAction<number>>;
}

const BasicDetails: React.FC<BasicDetailsProps> = ({
  selectedData,
  schoolName,
  logo,
  assignedLicenses,
  contacts,
  admins,
  setSchoolName,
  setLogo,
  setAssignedLicenses,
  setContacts,
  setAdmins,
  setStage,
}) => {
  // States definition
  const [nextAvailable, setNextAvailable] = useState(true);
  const [showAddContactsPopUp, setShowAddContactsPopUp] = useState(false);
  const [showEditContact, setShowEditContact] = useState(false);
  // State for edit contact
  const [contactToEdit, setContactToEdit] = useState<ContactListType | null>(
    null
  );

  // Parameter definition
  const fromCreateNew = !selectedData;

  // Define the email to request the licenses
  const { title, licensesEmail, schoolTerminology } = usePlatform();

  // Get the values from the context
  const { userRole, userFirstName, userLastName } = useContext(MainContext);

  // Parameter definition
  const mailToSubject = `${schoolName} has requested new ${title} licences`;
  const mailToBody = `Hi ${title},\n\n
    I would like to request:\n\n
    000 licences (please enter the number of licences you would like)\n\n
    Please call me on 0400 000 000 (please enter your best contact number) to confirm the start date and pricing.\n\n
    Thanks,\n\n
    ${userFirstName} ${userLastName}`;

  /**
   * Condition to activate the next button
   */
  useEffect(() => {
    setNextAvailable(!!schoolName);
  }, [schoolName, contacts]);

  /**
   * Go to the second stage
   */
  const handleNext = () => {
    if (nextAvailable) setStage(2);
  };

  /**
   * Function to apply when click on contacts
   */
  const handleClickAddContacts = () => {
    setShowAddContactsPopUp(true);
  };


  /**
   * Function used to operate on click of the edit/open button
   */
  const handleEditContact = (user: ContactListType) => {
    // Select the current data
    setContactToEdit(user);
    // And display the pop up
    setShowEditContact(true);
  };

  /**
   * Function used to operate on click of the delete button
   */
  const handleDeleteContact = (user: ContactListType) => {
    // Deleting the user with the current id
    const usersFiltered = contacts.filter(
      (currentUser: ContactListType) => {
        // Check if the user exists or if it's temporary
        if (user._id) return currentUser._id !== user._id
        else return currentUser.tempId !== user.tempId
      }
    );
    
    // Set the new contacts
    setContacts(usersFiltered);
  }

  /**
   * Function used to define the behaviour when editing contact inside
   * CreateEditContact
   * @param data 
   * @param processedData 
   * @returns 
   */
  const handleSaveEditContact = async (
    data: CreateEditContactType,
    processedData: CreateEditContactType,
  ) => {
    // Check if the contactToEdit is defined
    if (!contactToEdit) return;

    // Check if the school of context is new or created
    if (fromCreateNew) {
      // If it's not created in the database, just update
      // it in the current list
      // Update the one with the specified tempId
      const updatedContacts = contacts.map((contact: ContactListType) => {
        if (contact.tempId === contactToEdit.tempId) {
          return { ...data, tempId: contact.tempId };
        }
        return contact;
      });

      // And set the updated contacts
      setContacts(updatedContacts as ContactListType[]);

      // But also update the admins list
      const updatedAdmins = admins.map((admin: ContactListType) => {
        if (admin.tempId === contactToEdit.tempId) {
          return { ...data, tempId: admin.tempId };
        }
        return admin;
      });

      // And set the updated contacts
      setAdmins(updatedAdmins as ContactListType[]);
    }
    // For a currently existing school
    else {
      // Update in the database

      const response = await updateRecordRequest(
        { _id: contactToEdit._id, toUpdate: processedData },
        "/api/users/customer-admins"
      );

      // Check if the response is successful
      if (!response.successful) {
        // alert(response.message);
        return;
      }

      // But also, update the list of selected contacts (to display the update
      // in the cards)
      const updatedContacts = contacts.map((contact: ContactListType) => {
        // For the contact of interest to be edited, apply the update
        if (contact._id === contactToEdit._id) {
          return { ...data, _id: contact._id };
        }
        // Otherwise just return the original contact
        return contact;
      });

      // Set the new contacts
      setContacts(updatedContacts as ContactListType[]);
    }

    // And close the edit contact pop up
    setShowEditContact(false);
  };

  return (
    <div className="w-[562px] h-full p-6 rounded-3xl justify-start items-start gap-2 inline-flex">
      <div className="w-full flex-col justify-start items-start gap-12 inline-flex">
        <div className="self-stretch h-fit flex-col justify-start items-start gap-12 flex">
          <div className="self-stretch text-neutral-700 text-[23px] font-semibold font-sans leading-7">
            Basic details
          </div>
        </div>
        <div className="self-stretch h-fit flex-col justify-start items-start gap-5 flex">
          <div className="self-stretch text-neutral-500 text-base font-semibold font-sans leading-[19px]">
            {singlify(schoolTerminology)} details
          </div>
          <InputBadgeImage
            text={`Upload ${singlify(
              schoolTerminology
            ).toLowerCase()} logo image`}
            badgeImage={logo}
            setBadgeImage={setLogo}
          />
          <div className="self-stretch h-fit flex-col justify-start items-start gap-1.5 flex">
            <label
              htmlFor="school-name"
              className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide"
            >
              {singlify(schoolTerminology)} Name*
            </label>
            <div className="self-stretch bg-neutral-50 rounded-lg justify-start items-center">
              <input
                id="school-name"
                type="text"
                value={schoolName}
                onChange={(event) => setSchoolName(event.target.value)}
                className="w-full h-full p-3 text-neutral-700 placeholder:text-neutral-300 text-base font-sans leading-[19px] bg-transparent border-none focus:outline-none"
                placeholder={`Enter ${singlify(
                  schoolTerminology
                ).toLowerCase()} name`}
              />
            </div>
          </div>
          <div
            className={`flex-col justify-start items-start flex ${
              ["customer admin", "class admin"].includes(userRole)
                ? "gap-3"
                : "gap-1.5"
            }`}
          >
            <div className="self-stretch h-fit flex-col justify-start items-start flex">
              <label
                htmlFor="assigned-licenses"
                className="text-neutral-500 text-xs font-normal font-sans uppercase leading-[14px] tracking-wide"
              >
                Assigned Licenses*
              </label>
              {["customer admin", "class admin"].includes(userRole) && (
                <div className="self-stretch py-1 justify-center items-center gap-2 inline-flex">
                  <div className="h-6 justify-center items-center gap-2 flex">
                    <div className="w-6 h-6 relative">
                      <MdOutlineAccountBalanceWallet className="w-full h-full fill-neutral-500" />
                    </div>
                  </div>
                  <div className="grow shrink basis-0 text-neutral-700 text-lg font-semibold font-sans leading-snug">
                    {assignedLicenses}
                  </div>
                </div>
              )}
            </div>
            {userRole === "master admin" && (
              <div className="justify-center items-center gap-3 inline-flex">
                <div
                  className="p-2 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150 rounded-full justify-start items-end gap-2 flex cursor-pointer"
                  onClick={() =>
                    setAssignedLicenses(Math.max(0, assignedLicenses - 10))
                  }
                >
                  <div className="w-4 h-4 relative">
                    <MdRemove className="w-full h-full fill-neutral-700" />
                  </div>
                </div>
                <div className="self-stretch bg-neutral-25 rounded-lg justify-center items-center gap-2 flex">
                  <input
                    id="assigned-licenses"
                    className="w-[59px] h-full rounded-lg text-center text-neutral-500 text-base bg-transparent font-normal font-sans leading-[19px]"
                    type="text"
                    value={assignedLicenses}
                    onChange={(event) =>
                      setAssignedLicenses(parseInt(event.target.value) || 0)
                    }
                  />
                </div>
                <div
                  className="p-2 bg-neutral-50 hover:bg-neutral-100 ease-in-out duration-150  rounded-full justify-start items-end gap-2 flex cursor-pointer"
                  onClick={() => setAssignedLicenses(assignedLicenses + 10)}
                >
                  <div className="w-4 h-4 relative">
                    <MdAdd className="w-full h-full fill-neutral-700" />
                  </div>
                </div>
              </div>
            )}
            {userRole === "customer admin" && (
              <a
                className="group rounded justify-center items-center gap-2 inline-flex cursor-pointer"
                href={`mailto:${licensesEmail}?subject=${mailToSubject}&body=${encodeURIComponent(
                  mailToBody
                )}`}
                target="_blank"
              >
                <div className="w-6 h-6 relative">
                  <MdAddCircleOutline className="w-full h-full fill-secondary-600 group-hover:fill-secondary-700" />
                </div>
                <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
                  Request Additional Licenses
                </div>
              </a>
            )}
          </div>
        </div>
        <div className="self-stretch h-fit flex-col justify-start items-start gap-12 flex">
          <div className="self-stretch h-fit flex-col justify-start items-start gap-5 flex">
            <div className="self-stretch h-fit flex-col justify-start items-start gap-3 flex text-neutral-500 text-base font-semibold font-sans leading-[19px]">
              Contact details
            </div>
            {contacts.length === 0 ? (
              <button
                className="w-full px-2 py-3 rounded-lg border border-dashed border-neutral-300 hover:bg-neutral-25 ease-in-out duration-150 justify-center items-center gap-2 inline-flex cursor-pointer"
                onClick={handleClickAddContacts}
              >
                <div className="w-6 h-6 relative">
                  <MdOutlinePersonAddAlt className="w-full h-full fill-neutral-500" />
                </div>
                <div className="text-neutral-500 text-base font-normal font-sans leading-[19px]">
                  Add a contact
                </div>
              </button>
            ) : (
              <>
                {contacts.map((contact: ContactListType, index: number) => (
                  <UserCardForm
                    key={index}
                    user={contact}
                    selectedUsers={contacts}
                    handleDelete={handleDeleteContact}
                    handleEdit={handleEditContact}
                  />
                ))}
                <div
                  className="group rounded justify-center items-center gap-2 flex cursor-pointer"
                  onClick={handleClickAddContacts}
                >
                  <div className="w-6 h-6 relative">
                    <MdAddCircleOutline className="w-full h-full fill-secondary-600 group-hover:fill-secondary-700" />
                  </div>
                  <div className="text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug">
                    Add additional contact
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="self-stretch h-fit justify-end items-right inline-flex">
          <button
            className={`group rounded justify-center items-center gap-2 flex ${
              nextAvailable ? "cursor-pointer" : "cursor-not-allowed"
            } `}
            onClick={handleNext}
          >
            <div
              className={
                nextAvailable
                  ? "text-secondary-600 group-hover:text-secondary-700 text-lg font-semibold font-sans leading-snug"
                  : "text-neutral-200 text-lg font-semibold font-sans leading-snug"
              }
            >
              Next
            </div>
            <div className="w-6 h-6 relative">
              <MdOutlineArrowCircleRight
                className={
                  nextAvailable
                    ? "w-full h-full fill-secondary-600 group-hover:fill-secondary-700"
                    : "w-full h-full fill-neutral-200"
                }
              />
            </div>
          </button>
        </div>
      </div>
      <AnimatePresence>
        {/* Add contacts pop-up */}
        {showAddContactsPopUp && (
          <AddContactsPanel
            fromCreateNew={!selectedData}
            contacts={contacts}
            admins={admins}
            setContacts={setContacts}
            setAdmins={setAdmins}
            handleClosePopUp={() => setShowAddContactsPopUp(false)}
          />
        )}
        {/* Edit contacts pop-up */}
        {showEditContact && (
          <CreateEditUser
            confirmButtonText="Save contact"
            userToEdit={contactToEdit}
            showPrimaryOption={false}
            handlePressBack={() => setShowEditContact(false)}
            handleSaveFunction={handleSaveEditContact}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default BasicDetails;
